import { Link } from 'react-router'
import { type CatalogPlan, type PlanPrice } from '@/api/schemas/catalog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'
import { ENTERPRISE_CARD, PLAN_PRESENTATION, type PlanPresentation } from '../content'
import { annualMonthlyCents, annualTotalCents, resolvePlanPrice } from '../pricing'
import { type BillingCadence } from './PlanControls'

function fallbackPresentation(plan: CatalogPlan): PlanPresentation {
  // Unknown slugs still render sensibly, straight from the features map.
  return {
    blurb: '',
    bullets: Object.entries(plan.features)
      .filter(([key]) => key !== 'recommended')
      .map(([key, value]) => ({
        label: `${key.replaceAll('_', ' ')}: ${value}`,
        included: value !== 'false',
      })),
  }
}

function Bullet({
  label,
  included,
  strong,
  onDark = false,
}: {
  label: string
  included: boolean
  strong?: boolean | undefined
  onDark?: boolean
}) {
  return (
    <div className="flex gap-2.5 text-[12.5px] leading-snug">
      <span
        className={cn(
          'mt-[5px] size-1.5 shrink-0 rounded-full',
          included ? 'bg-accent' : onDark ? 'bg-white/25' : 'bg-line-strong',
        )}
        aria-hidden
      />
      <span
        className={cn(
          included ? (onDark ? 'text-console-fg' : 'text-content-body') : 'text-content-muted',
          strong && 'font-semibold ' + (onDark ? 'text-white' : 'text-content'),
        )}
      >
        {label}
      </span>
    </div>
  )
}

function PriceBlock({
  cents,
  currency,
  cadence,
}: {
  cents: number
  currency: string
  cadence: BillingCadence
}) {
  const displayCents = cadence === 'annual' ? annualMonthlyCents(cents) : cents
  return (
    <>
      <div className="mt-3.5 mb-1 flex items-end gap-1.5">
        <span className="text-[34px] leading-none font-semibold tracking-tight text-content">
          {formatMoney(displayCents, currency)}
        </span>
        <span className="text-xs text-content-muted">/mo</span>
      </div>
      <p className="mb-1.5 min-h-4 font-mono text-[11px] leading-snug text-content-muted">
        {cadence === 'annual' ? (
          <>
            {formatMoney(annualTotalCents(cents), currency)} billed yearly · was{' '}
            {formatMoney(cents, currency)}
          </>
        ) : null}
      </p>
    </>
  )
}

export interface PlanGridProps {
  plans: CatalogPlan[] | undefined
  prices: PlanPrice[] | undefined
  currencyCode: string
  cadence: BillingCadence
  isLoading?: boolean
  className?: string
}

/**
 * The plan card grid shared by the landing pricing section, /pricing, and
 * later the onboarding + P4 register pickers. Cards render from the catalog
 * API; the "MOST TEAMS" highlight comes from plan metadata
 * (features.recommended), never markup. Enterprise is a static dark card.
 */
export function PlanGrid({
  plans,
  prices,
  currencyCode,
  cadence,
  isLoading = false,
  className,
}: PlanGridProps) {
  if (isLoading || !plans) {
    return (
      <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="rounded-2xl border bg-surface-raised p-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="mt-4 h-9 w-28" />
            <Skeleton className="mt-6 h-3.5 w-full" />
            <Skeleton className="mt-2 h-3.5 w-4/5" />
            <Skeleton className="mt-2 h-3.5 w-5/6" />
            <Skeleton className="mt-7 h-11 w-full rounded-[10px]" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {plans.map((plan) => {
        const highlighted = plan.features['recommended'] === 'true'
        const presentation = PLAN_PRESENTATION[plan.slug] ?? fallbackPresentation(plan)
        const price = resolvePlanPrice(plan, currencyCode, prices)
        return (
          <article
            key={plan.slug}
            data-plan={plan.slug}
            className={cn(
              'card-lift relative flex flex-col rounded-2xl border bg-surface-raised p-6',
              highlighted && 'border-[1.5px] border-brand shadow-[0_14px_36px_rgb(30_42_94/0.15)]',
            )}
          >
            {highlighted ? (
              <span className="absolute -top-3 left-6 rounded-md bg-brand px-2.5 py-1 font-mono text-[10px] font-bold tracking-wider text-white">
                MOST TEAMS
              </span>
            ) : null}
            <h3 className="text-[14.5px] font-semibold text-content">{plan.name}</h3>
            <PriceBlock cents={price.cents} currency={price.currency} cadence={cadence} />
            <p className="mb-5 text-xs leading-normal text-content-secondary">
              {presentation.blurb}
            </p>
            <div className="flex flex-col gap-2.5">
              {presentation.bullets.map((bullet) => (
                <Bullet key={bullet.label} {...bullet} />
              ))}
            </div>
            <div className="mt-auto pt-6">
              <Link
                to="/signup"
                className={cn(
                  'flex h-11 items-center justify-center rounded-[10px] text-[13.5px] font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  highlighted
                    ? 'bg-brand text-white hover:bg-brand-hover'
                    : 'border border-line-strong text-content hover:bg-accent-ui',
                )}
              >
                Start free
              </Link>
              {presentation.ctaNote ? (
                <p className="mt-2 text-center text-[11.5px] text-content-muted">
                  {presentation.ctaNote}
                </p>
              ) : null}
            </div>
          </article>
        )
      })}

      <article
        data-plan="enterprise"
        className="sidebar-texture card-lift relative flex flex-col overflow-hidden rounded-2xl bg-sidebar p-6"
      >
        <h3 className="text-[14.5px] font-semibold text-white">{ENTERPRISE_CARD.name}</h3>
        <div className="mt-3.5 mb-1 text-[32px] leading-none font-semibold tracking-tight text-white">
          {ENTERPRISE_CARD.priceLabel}
        </div>
        <p className="mb-1.5 h-4 font-mono text-[11px] text-sidebar-muted">
          {ENTERPRISE_CARD.priceNote}
        </p>
        <p className="mb-5 text-xs leading-normal text-console-muted">{ENTERPRISE_CARD.blurb}</p>
        <div className="flex flex-col gap-2.5">
          {ENTERPRISE_CARD.bullets.map((label) => (
            <Bullet key={label} label={label} included onDark />
          ))}
        </div>
        <div className="mt-auto pt-6">
          <a
            href="mailto:sales@fenceauth.com"
            className="flex h-11 items-center justify-center rounded-[10px] bg-accent text-[13.5px] font-semibold text-accent-ink transition-colors outline-none hover:bg-accent/90 focus-visible:ring-2 focus-visible:ring-ring"
          >
            {ENTERPRISE_CARD.ctaLabel}
          </a>
          <p className="mt-2 text-center text-[11.5px] text-sidebar-label">
            {ENTERPRISE_CARD.ctaNote}
          </p>
        </div>
      </article>
    </div>
  )
}
